import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../App';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

const ACTION_STYLES = {
  create: 'bg-emerald-50 text-emerald-700',
  update: 'bg-blue-50 text-blue-700',
  delete: 'bg-rose-50 text-rose-700',
  assign: 'bg-violet-50 text-violet-700',
  escalate: 'bg-amber-50 text-amber-700',
};
const styleFor = (a) => {
  const key = Object.keys(ACTION_STYLES).find((k) => (a || '').startsWith(k));
  return ACTION_STYLES[key] || 'bg-slate-100 text-slate-600';
};
const fmtVal = (v) => (v == null ? '—' : (typeof v === 'object' ? JSON.stringify(v) : String(v)));

const AuditLog = () => {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [actionFilter, setActionFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const params = actionFilter !== 'all' ? { action: actionFilter } : {};
      const { data } = await axios.get(`${API}/audit`, { params });
      setLogs(data);
    } catch (e) {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [actionFilter]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { axios.get(`${API}/audit/actions`).then((r) => setActions(r.data)).catch(() => {}); }, []);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="audit-log-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2">Audit Log</h1>
          <p className="text-lg text-slate-600">Complete admin activity history for compliance</p>
        </div>
        <Select value={actionFilter} onValueChange={setActionFilter}>
          <SelectTrigger className="w-56" data-testid="audit-action-filter"><SelectValue placeholder="Filter action" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {actions.map((a) => <SelectItem key={a} value={a}>{a.replace(/_/g, ' ')}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-indigo-600" />{logs.length} record(s)</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <p className="text-center text-slate-500 py-8">Loading...</p>
          ) : logs.length === 0 ? (
            <p className="text-center text-slate-500 py-8">No audit records yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="audit-table">
                <thead>
                  <tr className="text-left text-slate-500 border-b">
                    <th className="py-2 pr-4">Date/Time</th>
                    <th className="py-2 px-2">User</th>
                    <th className="py-2 px-2">Role</th>
                    <th className="py-2 px-2">Action</th>
                    <th className="py-2 px-2">Previous</th>
                    <th className="py-2 px-2">New</th>
                  </tr>
                </thead>
                <tbody>
                  {logs.map((l) => (
                    <tr key={l.id} className="border-b border-slate-50 hover:bg-slate-50 align-top" data-testid={`audit-row-${l.id}`}>
                      <td className="py-2 pr-4 whitespace-nowrap text-slate-500">{(l.created_at || '').replace('T', ' ').slice(0, 16)}</td>
                      <td className="py-2 px-2 font-medium text-slate-800">{l.user_name}</td>
                      <td className="py-2 px-2 text-slate-500">{(l.user_role || '').replace(/_/g, ' ')}</td>
                      <td className="py-2 px-2"><Badge className={styleFor(l.action)}>{(l.action || '').replace(/_/g, ' ')}</Badge></td>
                      <td className="py-2 px-2 text-xs text-slate-400 max-w-[180px] break-words">{fmtVal(l.previous_value)}</td>
                      <td className="py-2 px-2 text-xs text-slate-600 max-w-[180px] break-words">{fmtVal(l.new_value)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLog;
